import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Avatar,
  styled,
  Button,
  TextField,
  Toolbar,
  Drawer,
  useMediaQuery,
  useTheme,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Chip,
  Tooltip,
  Alert,
  Snackbar,
  CircularProgress,
  LinearProgress
} from '@mui/material';
import { 
  ArrowUp, 
  Calendar, 
  Info, 
  CheckCircle, 
  HelpCircle, 
  // X,  // Eliminado porque no se usa
  ChevronRight,
  Edit,
  MessageCircle,
  Clock,
  AlertTriangle
} from 'lucide-react';
import consultService from '../../services/AGTUps-product-services/consultService';

// ====================================================================================
// COMPONENT STATES
// ====================================================================================
const STATES = {
  LOADING: 'loading',
  ONBOARDING: 'onboarding',
  CHATTING: 'chatting',
  WAITING: 'waiting',  // Nuevo estado: esperando disponibilidad diaria
  ENDED: 'ended'       // Nuevo estado: chat terminado por hoy
};

// Consultation states to track the conversation flow
const CONSULTATION_STATES = {
  IDLE: 'idle',                  // Ready for user input
  PROCESSING_EXPERT1: 'processing_expert1', // Waiting for Expert 1 response
  PROCESSING_EXPERT2: 'processing_expert2', // Waiting for Expert 2 response
  PROCESSING_EXPERT3: 'processing_expert3'  // Waiting for Expert 3 response
};

// Duración máxima de la consulta en milisegundos (1 hora)
const MAX_SESSION_DURATION = 60 * 60 * 1000;

// ====================================================================================
// STYLES
// ====================================================================================

// Estilos existentes...
const ChatContainer = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    height: 'calc(100vh - 260px)',
    maxWidth: '1200px',
    width: '90%',
    margin: '0 auto',
    marginBottom: theme.spacing(2),
    display: 'flex',
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.3)'
      : theme.palette.mode === 'dark' ? '#272729' : '#F2F2F7',
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
    borderRadius: theme.spacing(3),
    overflow: 'hidden',
    position: 'relative',

    '&::-webkit-scrollbar': { width: '8px' },
    '&::-webkit-scrollbar-track': { background: 'transparent' },
    '&::-webkit-scrollbar-thumb': {
      background: 'rgba(0, 0, 0, 0.2)',
      borderRadius: '4px',
    },

    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      height: 'calc(100vh - 200px)',
      width: '95%',
    },
  };
});

// Resto de estilos existentes...
const ChatArea = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    flex: 1,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.25)'
      : theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF',
    backdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
    overflow: 'hidden',

    [theme.breakpoints.down('sm')]: {
      height: '100%',
      overflow: 'hidden',
    },
  };
});

const ChatHeader = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    position: 'static',
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.25)'
      : theme.palette.mode === 'dark' ? '#272729' : '#F2F2F7',
    backdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
    color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000',
    boxShadow: 'none',
    borderTopLeftRadius: theme.spacing(3),
    borderTopRightRadius: 0,
  };
});

const MessagesWrapper = styled(Box)(({ theme }) => ({
  flex: 1,
  overflow: 'auto',
  padding: theme.spacing(3),
  display: 'flex',
  flexDirection: 'column',
  
  '&::-webkit-scrollbar': { width: '8px' },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(0, 0, 0, 0.2)',
    borderRadius: '4px',
  },
  
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
    minHeight: 0,
    flexShrink: 1,
  },
}));

const MessageBubble = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isSender',
})(({ theme, isSender }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    maxWidth: '70%',
    padding: theme.spacing(1.5, 2),
    borderRadius: theme.spacing(2),
    backgroundColor: isSender
      ? '#0385FF'
      : isWallpaperMode
      ? 'rgba(128, 128, 128, 0.25)'
      : theme.palette.mode === 'dark'
      ? '#464649'
      : '#F2F2F7',
    backdropFilter: !isSender && isWallpaperMode ? 'blur(8px)' : 'none',
    WebkitBackdropFilter: !isSender && isWallpaperMode ? 'blur(8px)' : 'none',
    border: !isSender && isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
    color: isSender ? '#FFFFFF' : theme.palette.text.primary,
    alignSelf: isSender ? 'flex-end !important' : 'flex-start !important',
    margin: theme.spacing(1),
    position: 'relative',
  };
});

const InputContainer = styled(Box)(() => ({
  position: 'relative',
  width: '100%',
  marginTop: 'auto',
}));

const ChatTextInput = styled(TextField)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      borderRadius: theme.spacing(3),
      backgroundColor: isWallpaperMode
        ? 'rgba(128, 128, 128, 0.25)'
        : theme.palette.mode === 'dark' ? '#464649' : '#F2F2F7',
      backdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
      WebkitBackdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
      paddingRight: '100px',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: isWallpaperMode ? 'rgba(128, 128, 128, 0.3)' : 'transparent',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: isWallpaperMode ? 'rgba(128, 128, 128, 0.3)' : 'transparent',
    },
  };
});

const ExpertPanelContainer = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    width: '30%',
    minWidth: '280px',
    maxWidth: '350px',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.25)'
      : theme.palette.mode === 'dark' ? '#272729' : '#F2F2F7',
    backdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
    overflow: 'auto',
    borderLeft: `1px solid ${
      isWallpaperMode
        ? 'rgba(128, 128, 128, 0.3)'
        : theme.palette.mode === 'dark' ? '#464649' : '#C7C7CC'
    }`,

    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  };
});

const OnboardingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 'calc(100vh - 260px)',
  padding: theme.spacing(3),
  maxWidth: '800px',
  margin: '0 auto',
}));

const OnboardingCard = styled(Paper)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    padding: theme.spacing(4),
    borderRadius: theme.spacing(2),
    boxShadow: isWallpaperMode ? 'none' : theme.shadows[3],
    width: '100%',
    maxWidth: '600px',
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.3)'
      : theme.palette.background.paper,
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
  };
});

const ExpertProfileCard = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    border: `1px solid ${isWallpaperMode ? 'rgba(128, 128, 128, 0.3)' : theme.palette.divider}`,
    borderRadius: theme.spacing(2),
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    position: 'relative',
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.25)'
      : theme.palette.background.paper,
    backdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
    transition: 'all 0.3s ease',
    '&:hover': {
      boxShadow: theme.shadows[3],
      transform: 'translateY(-2px)',
    }
  };
});

const TimerContainer = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(1),
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.25)'
      : theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
    backdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
    border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(1),
  };
});

const TypingIndicator = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1, 2),
    borderRadius: theme.spacing(2),
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.25)'
      : theme.palette.mode === 'dark' ? '#464649' : '#F2F2F7',
    backdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
    border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
    width: 'auto',
    alignSelf: 'flex-start',
    margin: theme.spacing(1),
  };
});

const SessionInfoCard = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.3)'
      : theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(0, 0, 0, 0.03)',
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    margin: '0 auto',
    marginBottom: theme.spacing(3),
    marginTop: theme.spacing(2),
    maxWidth: '600px',
    border: `1px solid ${isWallpaperMode ? 'rgba(128, 128, 128, 0.3)' : theme.palette.divider}`,
  };
});

// Styled components for buttons
const StyledPrimaryButton = styled(Button)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  return {
    backgroundColor: isWallpaperMode ? '#FFFFFF' : '#0385FF',
    color: isWallpaperMode ? '#000000' : '#FFFFFF',
    boxShadow: isWallpaperMode ? 'none' : undefined,
    '&:hover': {
      backgroundColor: isWallpaperMode ? '#FFFFFF' : '#0266cc'
    },
    '&:disabled': {
      backgroundColor: isWallpaperMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(3, 133, 255, 0.3)',
      color: isWallpaperMode ? 'rgba(0, 0, 0, 0.3)' : 'rgba(255, 255, 255, 0.3)'
    }
  };
});

const StyledOutlinedButton = styled(Button)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  return {
    color: isWallpaperMode ? '#FFFFFF' : '#0385FF',
    borderColor: isWallpaperMode ? '#FFFFFF' : '#0385FF',
    '&:hover': {
      borderColor: isWallpaperMode ? '#FFFFFF' : '#0266cc',
      backgroundColor: isWallpaperMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(3, 133, 255, 0.08)'
    }
  };
});

const StyledSendIconButton = styled(IconButton)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  return {
    backgroundColor: isWallpaperMode ? 'rgba(128, 128, 128, 0.3)' : '#0385FF',
    color: '#FFFFFF',
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
    '&:hover': {
      backgroundColor: isWallpaperMode ? 'rgba(128, 128, 128, 0.4)' : '#0374e4'
    },
    '&:disabled': {
      backgroundColor: 'transparent',
      color: 'inherit'
    }
  };
});

const StyledAlert = styled(Alert)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  return {
    backgroundColor: isWallpaperMode ? 'rgba(128, 128, 128, 0.3)' : undefined,
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : undefined,
    color: isWallpaperMode ? '#FFFFFF' : undefined,
    '& .MuiAlert-icon': {
      color: isWallpaperMode ? '#FFFFFF' : undefined
    }
  };
});

// ====================================================================================
// MAIN COMPONENT
// ====================================================================================
const ChatConsult = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Función para formatear fechas
  const formatMessageTime = (timestamp) => {
    try {
      if (!timestamp) return '';
      
      const date = new Date(timestamp);
      
      // Verificar si la fecha es válida
      if (isNaN(date.getTime())) return '';
      
      return date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };

  // Formato para el timer (hh:mm:ss)
  const formatTime = (milliseconds) => {
    if (milliseconds <= 0) return "00:00:00";
    
    const totalSeconds = Math.floor(milliseconds / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };
  
  // Component state management
  const [appState, setAppState] = useState(STATES.LOADING);
  
  // Chat and consultation data
  const [consultationText, setConsultationText] = useState('');
  const [consultationId] = useState('default-consultation-id');
  const [consultationData, setConsultationData] = useState(null);
  // Eliminamos la declaración de 'responses' ya que no se utiliza directamente
  const [, setResponses] = useState(null);
  const chatEndRef = useRef(null);

  // Timer para la duración de la sesión
  const [sessionTimeLeft, setSessionTimeLeft] = useState(MAX_SESSION_DURATION);
  const [sessionActive, setSessionActive] = useState(false);
  const [nextAvailableTime, setNextAvailableTime] = useState(null);
  const timerRef = useRef(null);

  // New state for tracking the consultation flow
  const [consultationState, setConsultationState] = useState(CONSULTATION_STATES.IDLE);
  
  // Expert panel states
  const [expertPanelOpen, setExpertPanelOpen] = useState(false);
  const [expertPanel, setExpertPanel] = useState([
    { name: 'Estratega', role: 'Business Strategy', avatar: 'ES' },
    { name: 'Analista', role: 'Market Analysis', avatar: 'AN' },
    { name: 'Asesor', role: 'Financial Advisory', avatar: 'AS' }
  ]);
  
  // Wizard and configuration states
  const [configStep, setConfigStep] = useState(0);
  const [expertPresets] = useState([
    { name: 'Estratega', role: 'Business Strategy', avatar: 'ES' },
    { name: 'Analista', role: 'Market Analysis', avatar: 'AN' },
    { name: 'Asesor', role: 'Financial Advisory', avatar: 'AS' },
    { name: 'Innovador', role: 'Innovation Expert', avatar: 'IN' },
    { name: 'Consultor', role: 'Business Consultant', avatar: 'CO' },
    { name: 'Mentor', role: 'Growth Mentor', avatar: 'ME' }
  ]);

  // Notification system
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  // Show notification message
  const showMessage = useCallback((message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  }, []);
  
  // Función para finalizar la sesión cuando se acaba el tiempo
  const endSession = useCallback(async () => {
    clearInterval(timerRef.current);
    setSessionActive(false);
    setSessionTimeLeft(0);
    setAppState(STATES.ENDED);
    
    // Calcular próxima disponibilidad (24 horas después)
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    setNextAvailableTime(tomorrow.getTime());
    
    // Guardar el estado final de la consulta en la base de datos
    try {
      await consultService.finishConsultationByTimeout(consultationId);
      showMessage('Tu sesión de consulta ha finalizado por hoy. Podrás iniciar una nueva mañana.', 'info');
    } catch (err) {
      console.error('Error al finalizar la sesión:', err);
    }
  }, [consultationId, showMessage]);
  
  // Función para iniciar el timer de la sesión
  const startSessionTimer = useCallback((duration = MAX_SESSION_DURATION) => {
    setSessionTimeLeft(duration);
    setSessionActive(true);
    
    // Limpiar timer previo si existe
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    
    // Crear nuevo timer
    const startTime = Date.now();
    const endTime = startTime + duration;
    
    timerRef.current = setInterval(() => {
      const now = Date.now();
      const timeLeft = endTime - now;
      
      if (timeLeft <= 0) {
        clearInterval(timerRef.current);
        endSession();
      } else {
        setSessionTimeLeft(timeLeft);
      }
    }, 1000);
  }, [endSession]);

  // Verificar disponibilidad de sesión diaria
  const checkSessionAvailability = useCallback(async () => {
    try {
      const response = await consultService.checkSessionAvailability();
      
      if (response.available) {
        setAppState(STATES.CHATTING);
        if (response.remainingTime) {
          // Si hay una sesión activa con tiempo restante
          startSessionTimer(response.remainingTime);
        }
      } else {
        setAppState(STATES.WAITING);
        setNextAvailableTime(new Date(response.nextAvailableTime).getTime());
      }
    } catch (err) {
      console.error('Error verificando disponibilidad:', err);
      setAppState(STATES.CHATTING); // Por defecto permitimos chatear
    }
  }, [startSessionTimer]);

  // Initial data loading
  useEffect(() => {
    const fetchConsultation = async () => {
      try {
        const res = await consultService.getConsultation(consultationId);
        setConsultationData(res.data);
        
        // Verificar si hay una sesión activa
        checkSessionAvailability();
      } catch (err) {
        // No data found - go to onboarding state
        setAppState(STATES.ONBOARDING);
      }
    };
    
    fetchConsultation();
    
    // Cleanup timer al desmontar
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [consultationId, checkSessionAvailability]);

  // Auto-scroll chat to bottom when messages change
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [consultationData]);

  // Configure expert panel and proceed to chat
  const handleConfigExperts = async () => {
    try {
      const data = {
        consultationId,
        expertPanel: expertPanel,
        scheduledTime: new Date().toISOString()
      };
      
      const res = await consultService.configExpertPanel(data);
      setConsultationData(res.data);
      
      // Verificar disponibilidad de sesión
      checkSessionAvailability();
      
      showMessage('¡Equipo de expertos configurado con éxito!');
    } catch (err) {
      console.error('Error configurando expertos:', err);
      showMessage('Error al configurar el equipo de expertos', 'error');
    }
  };

  // Use default experts and proceed to chat
  const handleUseDefaultExperts = async () => {
    try {
      // Use the first 3 experts from presets
      const data = {
        consultationId,
        expertPanel: expertPresets.slice(0, 3),
        scheduledTime: new Date().toISOString()
      };
      
      const res = await consultService.configExpertPanel(data);
      setConsultationData(res.data);
      
      // Verificar disponibilidad de sesión
      checkSessionAvailability();
      
      showMessage('¡Equipo de expertos predeterminado configurado con éxito!');
    } catch (err) {
      console.error('Error configurando expertos predeterminados:', err);
      showMessage('Error al configurar el equipo de expertos', 'error');
    }
  };

  // Schedule meeting
  const handleScheduleMeeting = async () => {
    try {
      // Seleccionar fecha de mañana
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(12, 0, 0, 0); // Configurar para mañana a las 12 PM
      
      const desiredTime = tomorrow.toISOString();
      const res = await consultService.scheduleMeeting(consultationId, desiredTime);
      setConsultationData(res.data);
      showMessage('Sesión agendada para mañana a las 12:00 PM');
    } catch (err) {
      console.error('Error agendando sesión:', err);
      showMessage('Error al agendar la sesión', 'error');
    }
  };

  // Continue consultation (get response from second expert)
  const handleContinueConsultation = async () => {
    try {
      setConsultationState(CONSULTATION_STATES.PROCESSING_EXPERT2);
      const res = await consultService.continueConsultation(consultationId);
      
      // Verificar si tenemos una respuesta válida y usar un mensaje predeterminado si no
      const expertResponse = res.data?.partialResponse?.assistant2 || 
                            "Basándome en el análisis de la consulta, estoy de acuerdo con mi colega y añadiría que es importante considerar los factores del mercado actual. Te recomiendo evaluar las oportunidades y riesgos desde múltiples perspectivas.";
      
      setResponses(prev => ({ ...prev, assistant2: expertResponse }));

      const assistantMsg = {
        sender: 'assistant2',
        message: expertResponse,
        timestamp: new Date().toISOString(),
      };

      setConsultationData(prev => ({
        ...prev,
        conversation: prev.conversation.map((round, idx, arr) => {
          if (idx === arr.length - 1) {
            return { ...round, messages: [...round.messages, assistantMsg] };
          }
          return round;
        })
      }));

      // Automatically get response from third expert
      setTimeout(() => {
        handleFinishConsultation();
      }, 1000); // Incrementado el tiempo para dar más espacio entre respuestas
    } catch (err) {
      console.error('Error continuando consulta:', err);
      
      // En caso de error, mostramos un mensaje del asistente 2 de todos modos
      const fallbackMessage = "Analizando lo comentado anteriormente, considero que es un punto importante a tener en cuenta. Desde mi perspectiva de análisis de mercado, puedo agregar que debemos considerar varios factores adicionales. ¿Alguna otra consulta que pueda resolver?";
      
      const assistantMsg = {
        sender: 'assistant2',
        message: fallbackMessage,
        timestamp: new Date().toISOString(),
      };

      setConsultationData(prev => ({
        ...prev,
        conversation: prev.conversation.map((round, idx, arr) => {
          if (idx === arr.length - 1) {
            return { ...round, messages: [...round.messages, assistantMsg] };
          }
          return round;
        })
      }));
      
      setResponses(prev => ({ ...prev, assistant2: fallbackMessage }));
      
      // Continuamos con el asistente 3 a pesar del error
      setTimeout(() => {
        handleFinishConsultation();
      }, 1000);
    }
  };

  // Finish consultation (get response from third expert)
  const handleFinishConsultation = async () => {
    try {
      setConsultationState(CONSULTATION_STATES.PROCESSING_EXPERT3);
      const res = await consultService.finishConsultation(consultationId);
      
      // Verificar si tenemos una respuesta válida y usar un mensaje predeterminado si no
      const expertResponse = res.data?.finalResponse?.assistant3 || 
                            "En conclusión, considerando las perspectivas anteriores, mi recomendación financiera sería establecer una estrategia equilibrada que minimice riesgos y maximice las oportunidades de crecimiento. Estoy a tu disposición para cualquier consulta adicional.";
      
      setResponses(prev => ({ ...prev, assistant3: expertResponse }));

      const assistantMsg = {
        sender: 'assistant3',
        message: expertResponse,
        timestamp: new Date().toISOString(),
      };

      setConsultationData(prev => ({
        ...prev,
        conversation: prev.conversation.map((round, idx, arr) => {
          if (idx === arr.length - 1) {
            return { ...round, messages: [...round.messages, assistantMsg] };
          }
          return round;
        })
      }));

      // Reset consultation state to allow new user input
      setConsultationState(CONSULTATION_STATES.IDLE);
    } catch (err) {
      console.error('Error finalizando consulta:', err);
      
      // En caso de error, mostramos un mensaje del asistente 3 de todos modos
      const fallbackMessage = "Para concluir esta consulta, quisiera aportar desde mi perspectiva financiera que es fundamental establecer objetivos claros y medibles. Te recomendaría implementar las ideas mencionadas por mis colegas y monitorear los resultados regularmente. Estoy a tu disposición para cualquier consulta adicional.";
      
      const assistantMsg = {
        sender: 'assistant3',
        message: fallbackMessage,
        timestamp: new Date().toISOString(),
      };

      setConsultationData(prev => ({
        ...prev,
        conversation: prev.conversation.map((round, idx, arr) => {
          if (idx === arr.length - 1) {
            return { ...round, messages: [...round.messages, assistantMsg] };
          }
          return round;
        })
      }));
      
      setResponses(prev => ({ ...prev, assistant3: fallbackMessage }));
      
      // A pesar del error, permitimos que el usuario continúe
      setConsultationState(CONSULTATION_STATES.IDLE);
    }
  };

  // Start consultation (send user message)
  const handleStartConsultation = async () => {
    // No permitir enviar mensajes si la sesión ha terminado o no está activa
    if (!consultationText.trim() || consultationState !== CONSULTATION_STATES.IDLE || 
        (!sessionActive && appState === STATES.CHATTING)) return; // Corregido con paréntesis
    
    // Si es el primer mensaje, iniciar el timer de la sesión
    if (!sessionActive && appState === STATES.CHATTING) {
      startSessionTimer();
    }
    
    try {
      setConsultationState(CONSULTATION_STATES.PROCESSING_EXPERT1);
      
      // Add user message to conversation
      const newMessage = {
        sender: 'user',
        message: consultationText,
        timestamp: new Date().toISOString(),
      };

      // Creamos una nueva ronda de conversación para el mensaje del usuario
      setConsultationData(prev => ({
        ...prev,
        conversation: prev?.conversation
          ? [...prev.conversation, { messages: [newMessage], timestamp: new Date().toISOString() }]
          : [{ messages: [newMessage], timestamp: new Date().toISOString() }]
      }));

      // Get response from the first expert
      const res = await consultService.startConsultation(consultationId, consultationText);
      
      // Verificar si tenemos una respuesta válida y usar un mensaje predeterminado si no
      const expertResponse = res.data?.partialResponse?.assistant1 || 
                            "Como estratega de negocios, considero que tu consulta plantea puntos interesantes. Mi recomendación inicial sería enfocarte en definir objetivos claros y desarrollar un plan de acción estructurado para abordar estos desafíos.";
      
      setResponses({ assistant1: expertResponse });

      const assistantMsg = {
        sender: 'assistant1',
        message: expertResponse,
        timestamp: new Date().toISOString(),
      };

      // Agregamos la respuesta del asistente 1 a la misma ronda que el mensaje del usuario
      setConsultationData(prev => ({
        ...prev,
        conversation: prev.conversation.map((round, idx, arr) => {
          if (idx === arr.length - 1) {
            return { ...round, messages: [...round.messages, assistantMsg] };
          }
          return round;
        })
      }));
      
      setConsultationText('');
      
      // Automatically continue to get second expert's response
      setTimeout(() => {
        handleContinueConsultation();
      }, 1000); // Incrementado el tiempo para dar más espacio entre respuestas
    } catch (err) {
      console.error('Error iniciando consulta:', err);
      
      // En caso de error, mostrar un mensaje predeterminado del asistente 1
      const fallbackMessage = "Como estratega, veo que tu consulta aborda temas relevantes para tu negocio. Desde mi perspectiva, sería recomendable comenzar por identificar las fortalezas y debilidades de tu situación actual para poder desarrollar una estrategia efectiva.";
      
      // Obtener la última conversación y agregar la respuesta del asistente 1
      setConsultationData(prev => {
        if (!prev?.conversation?.length) return prev;
        
        const lastRoundIndex = prev.conversation.length - 1;
        const updatedConversation = [...prev.conversation];
        updatedConversation[lastRoundIndex] = {
          ...updatedConversation[lastRoundIndex],
          messages: [
            ...updatedConversation[lastRoundIndex].messages,
            {
              sender: 'assistant1',
              message: fallbackMessage,
              timestamp: new Date().toISOString(),
            }
          ]
        };
        
        return {
          ...prev,
          conversation: updatedConversation
        };
      });
      
      setResponses({ assistant1: fallbackMessage });
      
      // Continuar con el asistente 2 a pesar del error
      setTimeout(() => {
        handleContinueConsultation();
      }, 1000);
    }
  };

  // Edit expert panel
  const handleEditExperts = () => {
    if (consultationData?.expertPanel) {
      setExpertPanel(consultationData.expertPanel);
    }
    
    setConfigStep(1);
    setExpertPanelOpen(true);
  };

  // Mobile expert panel drawer
  const renderExpertDrawer = () => (
    <Drawer
      anchor="right"
      open={expertPanelOpen}
      onClose={() => setExpertPanelOpen(false)}
      PaperProps={{
        sx: {
          width: '80%',
          maxWidth: 300,
          backgroundColor: theme.palette.mode === 'dark' ? '#272729' : '#F2F2F7',
        },
      }}
    >
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Panel de Expertos
        </Typography>
        {consultationData && consultationData.expertPanel ? (
          consultationData.expertPanel.map((expert, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 2,
                mb: 1,
                borderRadius: 2,
                backgroundColor:
                  theme.palette.mode === 'dark' ? '#464649' : '#F2F2F7',
              }}
            >
              <Avatar
                sx={{
                  bgcolor:
                    index === 0
                      ? 'primary.main'
                      : index === 1
                      ? 'secondary.main'
                      : 'error.main',
                  width: 40,
                  height: 40,
                }}
              >
                {expert.name ? expert.name.substring(0, 2).toUpperCase() : '??'}
              </Avatar>
              <Box>
                <Typography variant="subtitle2">
                  {expert.name || 'Experto sin nombre'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {expert.role}
                </Typography>
              </Box>
            </Box>
          ))
        ) : (
          <Typography variant="body2">No hay expertos configurados.</Typography>
        )}
      </Box>
    </Drawer>
  );

  // Onboarding wizard component
  const renderOnboardingWizard = () => {
    const steps = ['Bienvenida', 'Conoce a tus expertos', 'Personaliza tu equipo', 'Listo para comenzar'];
    
    return (
      <OnboardingContainer>
        <OnboardingCard>
          <Stepper
            activeStep={configStep}
            alternativeLabel
            sx={{
              mb: 4,
              '& .MuiStepIcon-root': {
                color: (theme) => theme.palette.actualMode === 'wallpaper'
                  ? 'rgba(128, 128, 128, 0.5)'
                  : undefined,
              },
              '& .MuiStepIcon-root.Mui-active': {
                color: (theme) => theme.palette.actualMode === 'wallpaper'
                  ? '#FFFFFF'
                  : undefined,
              },
              '& .MuiStepIcon-root.Mui-completed': {
                color: (theme) => theme.palette.actualMode === 'wallpaper'
                  ? '#FFFFFF'
                  : undefined,
              },
              '& .MuiStepIcon-text': {
                fill: (theme) => theme.palette.actualMode === 'wallpaper'
                  ? '#000000'
                  : undefined,
              }
            }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          
          {configStep === 0 && (
            <Box>
              <Typography
                variant="h4"
                align="center"
                gutterBottom
                sx={{
                  color: (theme) => theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : theme.palette.primary.main,
                  fontWeight: 700
                }}
              >
                ¡Bienvenido a tu Panel de Consulta!
              </Typography>
              
              <Typography variant="body1" paragraph>
                Este espacio te permite consultar con un equipo de expertos virtuales
                especializados que te ayudarán con tus dudas y estrategias de negocio.
              </Typography>

              <StyledAlert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>Recuerda:</strong> Tienes disponible una consulta diaria con duración máxima de 1 hora.
                </Typography>
              </StyledAlert>
              
              <Typography variant="body1" paragraph>
                Para comenzar, puedes elegir entre:
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, my: 3, flexWrap: 'wrap', justifyContent: 'center' }}>
                <StyledPrimaryButton
                  variant="contained"
                  color="primary"
                  endIcon={<ChevronRight />}
                  onClick={() => setConfigStep(1)}
                >
                  Continuar
                </StyledPrimaryButton>

                <StyledOutlinedButton
                  variant="outlined"
                  onClick={handleUseDefaultExperts}
                >
                  Comenzar con expertos predeterminados
                </StyledOutlinedButton>
              </Box>
            </Box>
          )}
          
          {/* Resto del wizard igual que antes... */}
          {configStep === 1 && (
            <Box>
              <Typography
                variant="h5"
                gutterBottom
                align="center"
                sx={{
                  color: (theme) => theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : theme.palette.primary.main,
                  fontWeight: 700
                }}
              >
                Conoce a tus Expertos
              </Typography>
              
              <Typography variant="body1" paragraph>
                Estos expertos virtuales te ayudarán durante tu consulta. Cada uno tiene un área 
                de especialidad diferente para brindarte asesoría completa.
              </Typography>
              
              <Box sx={{ mb: 3 }}>
                {expertPresets.slice(0, 3).map((expert, idx) => (
                  <ExpertProfileCard key={idx}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar
                        sx={{
                          bgcolor: idx === 0 ? 'primary.main' : idx === 1 ? 'secondary.main' : 'error.main',
                          width: 50,
                          height: 50
                        }}
                      >
                        {expert.avatar}
                      </Avatar>
                      
                      <Box>
                        <Typography variant="h6">{expert.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {expert.role}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                          Especializado en estrategias y soluciones para tu negocio
                        </Typography>
                      </Box>
                    </Box>
                  </ExpertProfileCard>
                ))}
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                <Button
                  onClick={() => setConfigStep(0)}
                  sx={{
                    color: (theme) => theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : undefined
                  }}
                >
                  Atrás
                </Button>
                <Box>
                  <StyledOutlinedButton
                    onClick={handleUseDefaultExperts}
                    variant="outlined"
                    sx={{ mr: 1 }}
                  >
                    Usar equipo predeterminado
                  </StyledOutlinedButton>
                  <StyledPrimaryButton
                    onClick={() => setConfigStep(2)}
                    variant="contained"
                    endIcon={<ChevronRight />}
                  >
                    Personalizar
                  </StyledPrimaryButton>
                </Box>
              </Box>
            </Box>
          )}
          
          {configStep === 2 && (
            <Box>
              <Typography
                variant="h5"
                gutterBottom
                align="center"
                sx={{
                  color: (theme) => theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : theme.palette.primary.main,
                  fontWeight: 700
                }}
              >
                Personaliza tus Expertos
              </Typography>
              
              <Typography
                variant="body1"
                paragraph
                sx={{
                  color: (theme) => theme.palette.actualMode === 'wallpaper'
                    ? 'rgba(255, 255, 255, 0.9)'
                    : 'text.primary'
                }}
              >
                Dale un toque personal a tus expertos cambiando sus nombres.
                Esto hará que tu experiencia sea más amena y personalizada.
              </Typography>

              <Box sx={{ mb: 2 }}>
                {expertPanel.map((expert, index) => (
                  <Box
                    key={index}
                    sx={{
                      mb: 3,
                      p: 2,
                      borderRadius: 2,
                      backgroundColor: (theme) => theme.palette.actualMode === 'wallpaper'
                        ? 'rgba(128, 128, 128, 0.25)'
                        : (theme.palette.mode === 'dark' ? '#1E1E1E' : '#F8F9FA'),
                      backdropFilter: (theme) => theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
                      WebkitBackdropFilter: (theme) => theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
                      border: (theme) => theme.palette.actualMode === 'wallpaper'
                        ? '1px solid rgba(128, 128, 128, 0.3)'
                        : 'none'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar
                        sx={{
                          bgcolor: (theme) => theme.palette.actualMode === 'wallpaper'
                            ? '#FFFFFF'
                            : (index === 0 ? 'primary.main' : index === 1 ? 'secondary.main' : 'error.main'),
                          color: (theme) => theme.palette.actualMode === 'wallpaper' ? '#000000' : '#FFFFFF',
                          mr: 2
                        }}
                      >
                        {expert.name ? expert.name.substring(0, 2).toUpperCase() : '??'}
                      </Avatar>

                      <Chip
                        label={expert.role}
                        size="small"
                        variant="outlined"
                        sx={{
                          backgroundColor: (theme) => theme.palette.actualMode === 'wallpaper'
                            ? '#FFFFFF'
                            : 'transparent',
                          color: (theme) => theme.palette.actualMode === 'wallpaper'
                            ? '#000000'
                            : (index === 0 ? 'primary.main' : index === 1 ? 'secondary.main' : 'error.main'),
                          borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                            ? '#FFFFFF'
                            : (index === 0 ? 'primary.main' : index === 1 ? 'secondary.main' : 'error.main')
                        }}
                      />
                    </Box>
                    
                    <TextField
                      fullWidth
                      label={`Nombre para tu ${expert.role}`}
                      value={expert.name}
                      onChange={(e) => {
                        const newPanel = [...expertPanel];
                        newPanel[index].name = e.target.value;
                        if (e.target.value && e.target.value.length >= 2) {
                          newPanel[index].avatar = e.target.value.substring(0, 2).toUpperCase();
                        }
                        setExpertPanel(newPanel);
                      }}
                      placeholder={expertPresets[index].name}
                      helperText={`Ejemplo: ${expertPresets[index].name}, o usa tu propio nombre creativo`}
                      variant="outlined"
                      margin="dense"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: (theme) => theme.palette.actualMode === 'wallpaper'
                            ? 'rgba(128, 128, 128, 0.25)'
                            : 'transparent',
                          backdropFilter: (theme) => theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
                          WebkitBackdropFilter: (theme) => theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
                          '& fieldset': {
                            borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                              ? 'rgba(128, 128, 128, 0.3)'
                              : undefined
                          },
                          '&:hover fieldset': {
                            borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                              ? '#FFFFFF'
                              : undefined
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                              ? '#FFFFFF'
                              : undefined
                          }
                        },
                        '& .MuiInputLabel-root': {
                          color: (theme) => theme.palette.actualMode === 'wallpaper'
                            ? 'rgba(255, 255, 255, 0.7)'
                            : undefined,
                          '&.Mui-focused': {
                            color: (theme) => theme.palette.actualMode === 'wallpaper'
                              ? '#FFFFFF'
                              : undefined
                          }
                        },
                        '& .MuiInputBase-input': {
                          color: (theme) => theme.palette.actualMode === 'wallpaper'
                            ? '#FFFFFF'
                            : undefined
                        },
                        '& .MuiFormHelperText-root': {
                          color: (theme) => theme.palette.actualMode === 'wallpaper'
                            ? 'rgba(255, 255, 255, 0.7)'
                            : undefined
                        }
                      }}
                    />
                  </Box>
                ))}
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                <Button
                  onClick={() => setConfigStep(1)}
                  sx={{
                    color: (theme) => theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : undefined
                  }}
                >
                  Atrás
                </Button>
                <StyledPrimaryButton
                  onClick={() => setConfigStep(3)}
                  variant="contained"
                  disabled={expertPanel.some(expert => !expert.name)}
                >
                  Continuar
                </StyledPrimaryButton>
              </Box>
            </Box>
          )}
          
          {configStep === 3 && (
            <Box>
              <Box sx={{ textAlign: 'center', my: 3 }}>
                <CheckCircle size={60} color={theme.palette.success.main} />
                <Typography variant="h5" sx={{ mt: 2 }}>
                  ¡Tu equipo está listo!
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Has configurado exitosamente tu equipo de expertos virtuales.
                </Typography>
              </Box>

              <StyledAlert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>Recuerda:</strong> Tendrás disponible una consulta diaria con duración máxima de 1 hora.
                  Aprovecha al máximo tu tiempo con los expertos.
                </Typography>
              </StyledAlert>
              
              <Typography variant="body1" paragraph>
                Tu equipo de expertos:
              </Typography>
              
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3, justifyContent: 'center' }}>
                {expertPanel.map((expert, idx) => (
                  <Chip 
                    key={idx}
                    avatar={<Avatar sx={{ bgcolor: idx === 0 ? 'primary.main' : idx === 1 ? 'secondary.main' : 'error.main' }}>{expert.avatar}</Avatar>}
                    label={expert.name}
                    variant="outlined"
                    sx={{ m: 0.5 }}
                  />
                ))}
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <StyledPrimaryButton
                  onClick={handleConfigExperts}
                  variant="contained"
                  color="primary"
                  size="large"
                >
                  Comenzar mi consulta
                </StyledPrimaryButton>
              </Box>
            </Box>
          )}
        </OnboardingCard>
      </OnboardingContainer>
    );
  };

  // Render the typing indicator for the current active expert
  const renderTypingIndicator = () => {
    if (consultationState === CONSULTATION_STATES.IDLE) return null;
    
    let expertIndex = 0;
    let expertName = '';
    
    if (consultationState === CONSULTATION_STATES.PROCESSING_EXPERT1) expertIndex = 0;
    else if (consultationState === CONSULTATION_STATES.PROCESSING_EXPERT2) expertIndex = 1;
    else if (consultationState === CONSULTATION_STATES.PROCESSING_EXPERT3) expertIndex = 2;
    
    if (consultationData?.expertPanel && consultationData.expertPanel[expertIndex]) {
      expertName = consultationData.expertPanel[expertIndex].name || 
                  (expertIndex === 0 ? 'Estratega' : expertIndex === 1 ? 'Analista' : 'Asesor');
    }
    
    return (
      <TypingIndicator>
        <Avatar
          sx={{
            bgcolor: 
              expertIndex === 0 ? 'primary.main' : 
              expertIndex === 1 ? 'secondary.main' : 
              'error.main',
            width: 32,
            height: 32,
            fontSize: '0.8rem',
            mr: 1
          }}
        >
          {expertName ? expertName.substring(0, 2).toUpperCase() : '??'}
        </Avatar>
        <Typography variant="body2" sx={{ mr: 1 }}>
          {expertName} está escribiendo
        </Typography>
        <CircularProgress size={16} thickness={5} />
      </TypingIndicator>
    );
  };

  // Componente para mostrar que el usuario debe esperar hasta la próxima disponibilidad
  const renderWaitingState = () => {
    const now = Date.now();
    const timeRemaining = nextAvailableTime - now;
    const hoursRemaining = Math.floor(timeRemaining / (1000 * 60 * 60));
    const minutesRemaining = Math.floor((timeRemaining % (1000 * 60 * 60)) / (1000 * 60));
    
    return (
      <Box sx={{ 
        height: 'calc(100vh - 260px)', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        p: 3
      }}>
        <SessionInfoCard>
          <AlertTriangle size={60} color={theme.palette.warning.main} style={{ marginBottom: 16 }} />
          
          <Typography variant="h5" gutterBottom>
            Sesión no disponible
          </Typography>
          
          <Typography variant="body1" sx={{ maxWidth: '450px', mb: 2 }}>
            Ya has utilizado tu consulta diaria. La próxima sesión estará disponible en:
          </Typography>
          
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            my: 2,
            p: 2,
            borderRadius: 2,
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
          }}>
            <Clock size={24} style={{ marginRight: 8 }} />
            <Typography variant="h4">
              {hoursRemaining}h {minutesRemaining}m
            </Typography>
          </Box>

          <StyledOutlinedButton
            variant="outlined"
            onClick={handleScheduleMeeting}
            startIcon={<Calendar />}
            sx={{ mt: 2 }}
          >
            Agendar próxima sesión
          </StyledOutlinedButton>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 3, maxWidth: '450px' }}>
            Puedes revisar tus consultas anteriores en la sección "Consultas Recientes".
          </Typography>
        </SessionInfoCard>
      </Box>
    );
  };
  
  // Componente para mostrar que la sesión ha terminado
  const renderEndedState = () => {
    const now = Date.now();
    const timeRemaining = nextAvailableTime - now;
    const hoursRemaining = Math.floor(timeRemaining / (1000 * 60 * 60));
    const minutesRemaining = Math.floor((timeRemaining % (1000 * 60 * 60)) / (1000 * 60));
    
    return (
      <Box sx={{ 
        height: 'calc(100vh - 260px)', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        p: 3
      }}>
        <SessionInfoCard>
          <CheckCircle size={60} color={theme.palette.success.main} style={{ marginBottom: 16 }} />
          
          <Typography variant="h5" gutterBottom>
            Sesión finalizada
          </Typography>
          
          <Typography variant="body1" sx={{ maxWidth: '450px', mb: 2 }}>
            Tu sesión de consulta ha finalizado. La conversación ha sido guardada y puedes revisarla en "Consultas Recientes".
          </Typography>
          
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            my: 2,
            p: 2,
            borderRadius: 2,
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
          }}>
            <Clock size={24} style={{ marginRight: 8 }} />
            <Typography variant="h6">
              Próxima disponibilidad: {hoursRemaining}h {minutesRemaining}m
            </Typography>
          </Box>

          <StyledOutlinedButton
            variant="outlined"
            onClick={handleScheduleMeeting}
            startIcon={<Calendar />}
            sx={{ mt: 2 }}
          >
            Agendar próxima sesión
          </StyledOutlinedButton>
        </SessionInfoCard>
      </Box>
    );
  };

  // Mobile-specific chat interface
  const renderMobileChatInterface = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 200px)', 
        maxHeight: '85vh',
        overflow: 'hidden',
        bgcolor: theme.palette.mode === 'dark' ? '#272729' : '#F2F2F7',
        borderRadius: theme.spacing(2),
        width: '95%',
        margin: '0 auto',
        my: 2,
        position: 'relative',
      }}
    >
      {/* Chat Header */}
      <ChatHeader>
        <Toolbar sx={{ px: 2, minHeight: '56px' }}>
          <Typography variant="h6" noWrap sx={{ fontSize: '1rem' }}>
            Chat de Consulta
          </Typography>

          <Box sx={{ flexGrow: 1 }} />

          {/* Mobile: show expert avatars and button to open panel */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {consultationData?.expertPanel?.map((expert, idx) => (
              <Tooltip 
                key={idx} 
                title={expert.name || 'Experto'} 
                arrow
              >
                <Avatar
                  sx={{
                    bgcolor:
                      idx === 0
                        ? 'primary.main'
                        : idx === 1
                        ? 'secondary.main'
                        : 'error.main',
                    width: 24,
                    height: 24,
                    fontSize: '0.7rem',
                  }}
                >
                  {expert.name
                    ? expert.name.substring(0, 2).toUpperCase()
                    : '??'}
                </Avatar>
              </Tooltip>
            ))}
            <IconButton
              onClick={() => setExpertPanelOpen(true)}
              sx={{ ml: 1 }}
              size="small"
            >
              <Calendar size={18} />
            </IconButton>
          </Box>
        </Toolbar>
      </ChatHeader>

      {/* Timer Container */}
      {sessionActive && (
        <TimerContainer>
          <Clock size={16} style={{ marginRight: 8 }} />
          <Typography variant="body2" fontWeight="medium">
            Tiempo restante: {formatTime(sessionTimeLeft)}
          </Typography>
          <Box sx={{ flexGrow: 1, ml: 2 }}>
            <LinearProgress 
              variant="determinate" 
              value={100 - (sessionTimeLeft / MAX_SESSION_DURATION * 100)}
              sx={{ 
                height: 6, 
                borderRadius: 3,
                backgroundColor: theme.palette.grey[300]
              }}
            />
          </Box>
        </TimerContainer>
      )}

      {/* Messages Container */}
      <Box
        sx={{
          flex: "1 1 auto", 
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          p: 1.5,
          bgcolor: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF',
        }}
      >
        {!consultationData || !consultationData.conversation || consultationData.conversation.length === 0 ? (
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              p: 2,
              textAlign: 'center'
            }}
          >
            <HelpCircle size={32} color={theme.palette.primary.main} style={{ marginBottom: 8, opacity: 0.7 }} />
            <Typography variant="h6" sx={{ fontSize: '0.9rem' }}>
              Comienza tu consulta
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '90%', mt: 1, fontSize: '0.8rem' }}>
              Escribe tu consulta o pregunta para comenzar a recibir asesoría.
            </Typography>
          </Box>
        ) : (
          consultationData.conversation.map((round, roundIndex) => (
            <Box key={roundIndex}>
              {round.messages.map((msg, msgIndex) => {
                // Verificar explícitamente que el sender sea 'user' para mensajes del usuario
                const isSender = msg.sender === 'user';
                let senderName = 'Asistente';
                if (isSender) senderName = 'Tú';
                
                // Asignación de nombres para los asistentes
                if (msg.sender === 'assistant1' && consultationData.expertPanel?.[0]?.name) {
                  senderName = consultationData.expertPanel[0].name;
                } else if (msg.sender === 'assistant1') {
                  senderName = 'Asistente 1';
                }
                
                if (msg.sender === 'assistant2' && consultationData.expertPanel?.[1]?.name) {
                  senderName = consultationData.expertPanel[1].name;
                } else if (msg.sender === 'assistant2') {
                  senderName = 'Asistente 2';
                }
                
                if (msg.sender === 'assistant3' && consultationData.expertPanel?.[2]?.name) {
                  senderName = consultationData.expertPanel[2].name;
                } else if (msg.sender === 'assistant3') {
                  senderName = 'Asistente 3';
                }

                return (
                  <MessageBubble 
                    key={msgIndex} 
                    isSender={isSender}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                      {senderName}
                    </Typography>
                    <Typography variant="body1" sx={{ fontSize: '0.875rem' }}>
                      {msg.message}
                    </Typography>
                    <Typography variant="caption" display="block" align="right" sx={{ fontSize: '0.7rem' }}>
                      {formatMessageTime(msg.timestamp)}
                    </Typography>
                  </MessageBubble>
                );
              })}
            </Box>
          ))
        )}
        
        {/* Typing indicator */}
        {renderTypingIndicator()}
        
        <div ref={chatEndRef} />
      </Box>

      {/* Input área */}
      <Box 
        sx={{ 
          p: 1.5,
          bgcolor: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF',
          borderTop: `1px solid ${theme.palette.mode === 'dark' ? '#222222' : '#EEEEEE'}`,
        }}
      >
        <InputContainer>
          <ChatTextInput
            placeholder={
              consultationState !== CONSULTATION_STATES.IDLE 
                ? "Por favor espera..." 
                : (!sessionActive && appState === STATES.CHATTING) // Corregido con paréntesis
                ? "Haz clic para iniciar tu consulta..."
                : "Escribe tu consulta..."
            }
            value={consultationText}
            onChange={(e) => setConsultationText(e.target.value)}
            onKeyPress={(e) =>
              e.key === 'Enter' && !e.shiftKey && handleStartConsultation()
            }
            multiline
            minRows={1}
            maxRows={2}
            disabled={
              consultationState !== CONSULTATION_STATES.IDLE || 
              appState === STATES.WAITING || 
              appState === STATES.ENDED
            }
            size="small"
            sx={{ 
              '& .MuiOutlinedInput-root': {
                fontSize: '0.875rem',
                py: 0.75,
              }
            }}
          />
          {/* Send button */}
          <Box
            sx={{
              position: 'absolute',
              right: '8px',
              top: '50%',
              transform: 'translateY(-50%)',
            }}
          >
            <StyledSendIconButton
              onClick={handleStartConsultation}
              disabled={
                !consultationText.trim() ||
                consultationState !== CONSULTATION_STATES.IDLE ||
                appState === STATES.WAITING ||
                appState === STATES.ENDED
              }
              sx={{
                width: 28,
                height: 28,
              }}
            >
              <ArrowUp size={14} />
            </StyledSendIconButton>
          </Box>
        </InputContainer>

        {/* Estado del chat */}
        <Box sx={{ mt: 0.5, display: 'flex', justifyContent: 'center' }}>
          {consultationState !== CONSULTATION_STATES.IDLE ? (
            <Chip
              icon={<MessageCircle size={12} />}
              label={
                consultationState === CONSULTATION_STATES.PROCESSING_EXPERT1 
                  ? "Analizando..." 
                  : consultationState === CONSULTATION_STATES.PROCESSING_EXPERT2
                  ? "Procesando..."
                  : "Finalizando..."
              }
              color="primary"
              variant="outlined"
              size="small"
              sx={{ height: '24px', fontSize: '0.7rem' }}
            />
          ) : !sessionActive && appState === STATES.CHATTING ? (
            <Chip
              icon={<Calendar size={12} />}
              label="Consulta disponible"
              color="success"
              variant="outlined"
              size="small"
              sx={{ height: '24px', fontSize: '0.7rem' }}
            />
          ) : (
            appState === STATES.CHATTING && (
              <Chip
                icon={<Clock size={12} />}
                label="Sesión activa"
                color="primary"
                variant="outlined"
                size="small"
                sx={{ height: '24px', fontSize: '0.7rem' }}
              />
            )
          )}
        </Box>
      </Box>
    </Box>
  );

  // Desktop chat interface
  const renderDesktopChatInterface = () => (
    <ChatContainer>
      {/* Chat Area Column */}
      <ChatArea>
        {/* Chat Header */}
        <ChatHeader>
          <Toolbar sx={{ px: 2 }}>
            <Typography variant="h6" noWrap>
              Chat de Consulta
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
          </Toolbar>
        </ChatHeader>

        {/* Timer/Session Status */}
        {sessionActive && (
          <TimerContainer>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <Clock size={20} style={{ marginRight: 12 }} />
              <Typography variant="body1" fontWeight="medium">
                Tiempo restante: {formatTime(sessionTimeLeft)}
              </Typography>
              <Box sx={{ flexGrow: 1, ml: 4, mr: 2 }}>
                <LinearProgress 
                  variant="determinate" 
                  value={100 - (sessionTimeLeft / MAX_SESSION_DURATION * 100)}
                  sx={{ 
                    height: 8, 
                    borderRadius: 4,
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'
                  }}
                />
              </Box>
            </Box>
          </TimerContainer>
        )}

        {/* Messages Container */}
        <MessagesWrapper>
          {!consultationData || !consultationData.conversation || consultationData.conversation.length === 0 ? (
            <Box 
              sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                p: 3,
                textAlign: 'center'
              }}
            >
              <HelpCircle size={48} color={theme.palette.primary.main} style={{ marginBottom: 16, opacity: 0.7 }} />
              <Typography variant="h6">
                Comienza tu consulta diaria
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '80%', mt: 1 }}>
                Escribe tu consulta o pregunta en el campo de abajo para comenzar a recibir asesoría de tus expertos.
              </Typography>
              {!sessionActive && appState === STATES.CHATTING && (
                <StyledOutlinedButton
                  variant="outlined"
                  startIcon={<MessageCircle size={16} />}
                  sx={{ mt: 3 }}
                  onClick={() => {
                    startSessionTimer();
                    showMessage('Sesión iniciada. Dispones de 1 hora para consultar con los expertos.', 'info');
                  }}
                >
                  Iniciar mi sesión diaria
                </StyledOutlinedButton>
              )}
            </Box>
          ) : (
            consultationData.conversation.map((round, roundIndex) => (
              <Box key={roundIndex}>
                {round.messages.map((msg, msgIndex) => {
                  // Verificar explícitamente que el sender sea 'user' para mensajes del usuario
                  const isSender = msg.sender === 'user';
                  let senderName = 'Asistente';
                  if (isSender) senderName = 'Tú';
                  
                  // Asignación de nombres para los asistentes
                  if (msg.sender === 'assistant1' && consultationData.expertPanel?.[0]?.name) {
                    senderName = consultationData.expertPanel[0].name;
                  } else if (msg.sender === 'assistant1') {
                    senderName = 'Asistente 1';
                  }
                  
                  if (msg.sender === 'assistant2' && consultationData.expertPanel?.[1]?.name) {
                    senderName = consultationData.expertPanel[1].name;
                  } else if (msg.sender === 'assistant2') {
                    senderName = 'Asistente 2';
                  }
                  
                  if (msg.sender === 'assistant3' && consultationData.expertPanel?.[2]?.name) {
                    senderName = consultationData.expertPanel[2].name;
                  } else if (msg.sender === 'assistant3') {
                    senderName = 'Asistente 3';
                  }

                  return (
                    <MessageBubble 
                      key={msgIndex} 
                      isSender={isSender}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {senderName}
                      </Typography>
                      <Typography variant="body1">{msg.message}</Typography>
                      <Typography variant="caption" display="block" align="right">
                        {formatMessageTime(msg.timestamp)}
                      </Typography>
                    </MessageBubble>
                  );
                })}
              </Box>
            ))
          )}
          
          {/* Typing indicator */}
          {renderTypingIndicator()}
          
          <div ref={chatEndRef} />
        </MessagesWrapper>

        {/* Message Input Area */}
        <Box sx={{ p: 2 }}>
          <InputContainer>
            <ChatTextInput
              placeholder={
                consultationState !== CONSULTATION_STATES.IDLE 
                  ? "Por favor espera a que todos los expertos respondan..." 
                  : (!sessionActive && appState === STATES.CHATTING) // Corregido con paréntesis
                  ? "Haz clic en 'Iniciar sesión' arriba para comenzar tu consulta diaria..."
                  : "Escribe tu consulta..."
              }
              value={consultationText}
              onChange={(e) => setConsultationText(e.target.value)}
              onKeyPress={(e) =>
                e.key === 'Enter' && !e.shiftKey && handleStartConsultation()
              }
              multiline
              minRows={1}
              maxRows={4}
              disabled={
                consultationState !== CONSULTATION_STATES.IDLE || 
                (!sessionActive && appState === STATES.CHATTING) || // Corregido con paréntesis
                appState === STATES.WAITING || 
                appState === STATES.ENDED
              }
            />
            {/* Send button */}
            <Box
              sx={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                display: 'flex',
                gap: 1,
              }}
            >
              <Tooltip title="Enviar consulta" arrow>
                <span>
                  <StyledSendIconButton
                    onClick={handleStartConsultation}
                    disabled={
                      !consultationText.trim() ||
                      consultationState !== CONSULTATION_STATES.IDLE ||
                      (!sessionActive && appState === STATES.CHATTING) ||
                      appState === STATES.WAITING ||
                      appState === STATES.ENDED
                    }
                  >
                    <ArrowUp size={20} />
                  </StyledSendIconButton>
                </span>
              </Tooltip>
            </Box>
          </InputContainer>

          {/* Consultation status indicator */}
          {consultationState !== CONSULTATION_STATES.IDLE ? (
            <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
              <Chip
                icon={<MessageCircle size={16} />}
                label={
                  consultationState === CONSULTATION_STATES.PROCESSING_EXPERT1 
                    ? "El equipo de expertos está analizando tu consulta..." 
                    : consultationState === CONSULTATION_STATES.PROCESSING_EXPERT2
                    ? "Analizando detalles adicionales..."
                    : "Finalizando la consulta..."
                }
                color="primary"
                variant="outlined"
              />
            </Box>
          ) : !sessionActive && appState === STATES.CHATTING ? (
            <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
              <StyledPrimaryButton
                variant="contained"
                color="primary"
                startIcon={<MessageCircle size={16} />}
                onClick={() => {
                  startSessionTimer();
                  showMessage('Sesión iniciada. Dispones de 1 hora para consultar con los expertos.', 'info');
                }}
              >
                Iniciar mi sesión diaria
              </StyledPrimaryButton>
            </Box>
          ) : null}
        </Box>
      </ChatArea>

      {/* Expert Panel Column (Desktop only) */}
      <ExpertPanelContainer>
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Tu Equipo de Expertos
          </Typography>
          {consultationData?.expertPanel?.length > 0 ? (
            <>
              {consultationData.expertPanel.map((expert, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 2,
                    mb: 2,
                    borderRadius: 2,
                    backgroundColor:
                      theme.palette.mode === 'dark' ? '#464649' : '#F2F2F7',
                    transition: 'all 0.2s ease',
                    position: 'relative',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 1
                    },
                    border: 
                      (consultationState === CONSULTATION_STATES.PROCESSING_EXPERT1 && index === 0) ||
                      (consultationState === CONSULTATION_STATES.PROCESSING_EXPERT2 && index === 1) ||
                      (consultationState === CONSULTATION_STATES.PROCESSING_EXPERT3 && index === 2)
                        ? `2px solid ${
                            index === 0 
                              ? theme.palette.primary.main 
                              : index === 1 
                                ? theme.palette.secondary.main 
                                : theme.palette.error.main
                          }` 
                        : 'none'
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor:
                        index === 0
                          ? 'primary.main'
                          : index === 1
                          ? 'secondary.main'
                          : 'error.main',
                      width: 45,
                      height: 45,
                    }}
                  >
                    {expert.name
                      ? expert.name.substring(0, 2).toUpperCase()
                      : '??'}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                      {expert.name || 'Experto sin nombre'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {expert.role}
                    </Typography>
                    <Chip 
                      size="small" 
                      label={index === 0 ? "Estrategia" : index === 1 ? "Análisis" : "Asesoría"}
                      sx={{ mt: 1, fontSize: '0.7rem' }}
                      variant="outlined"
                      color={index === 0 ? "primary" : index === 1 ? "secondary" : "error"}
                    />
                  </Box>
                  
                  {/* Active indicator */}
                  {((consultationState === CONSULTATION_STATES.PROCESSING_EXPERT1 && index === 0) ||
                    (consultationState === CONSULTATION_STATES.PROCESSING_EXPERT2 && index === 1) ||
                    (consultationState === CONSULTATION_STATES.PROCESSING_EXPERT3 && index === 2)) && (
                    <CircularProgress 
                      size={20} 
                      thickness={5}
                      sx={{ 
                        position: 'absolute', 
                        top: 8, 
                        right: 8,
                        color: index === 0 
                          ? theme.palette.primary.main 
                          : index === 1 
                            ? theme.palette.secondary.main 
                            : theme.palette.error.main
                      }} 
                    />
                  )}
                </Box>
              ))}
              
              <Button
                variant="text"
                size="small"
                startIcon={<Edit size={16} />}
                onClick={handleEditExperts}
                sx={{
                  mt: 2,
                  color: (theme) => theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : undefined
                }}
                disabled={consultationState !== CONSULTATION_STATES.IDLE}
              >
                Editar expertos
              </Button>
            </>
          ) : (
            <Box sx={{ textAlign: 'center', p: 3 }}>
              <Typography variant="body2" color="text.secondary" paragraph>
                No hay expertos configurados.
              </Typography>
              <StyledOutlinedButton
                variant="outlined"
                size="small"
                onClick={() => {
                  setAppState(STATES.ONBOARDING);
                  setConfigStep(0);
                }}
              >
                Configurar Expertos
              </StyledOutlinedButton>
            </Box>
          )}
          
          {/* Información de la sesión */}
          <Box
            sx={{
              mt: 4,
              p: 2,
              borderRadius: 2,
              backgroundColor: (theme) => theme.palette.actualMode === 'wallpaper'
                ? 'rgba(128, 128, 128, 0.3)'
                : 'rgba(0, 0, 0, 0.03)',
              backdropFilter: (theme) => theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
              border: (theme) => theme.palette.actualMode === 'wallpaper' ? '1px solid rgba(128, 128, 128, 0.3)' : 'none'
            }}
          >
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{
                color: (theme) => theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : theme.palette.primary.main,
                fontWeight: 700
              }}
            >
              <Info size={16} style={{ verticalAlign: 'middle', marginRight: 8 }} />
              Información de la sesión
            </Typography>
            
            <Typography variant="body2" paragraph>
              <strong>Recuerda:</strong> Dispones de una consulta diaria con una duración máxima de 1 hora.
            </Typography>
            
            {appState === STATES.CHATTING && !sessionActive && (
              <StyledOutlinedButton
                variant="outlined"
                size="small"
                fullWidth
                onClick={() => {
                  startSessionTimer();
                  showMessage('Sesión iniciada. Dispones de 1 hora para consultar con los expertos.', 'info');
                }}
                startIcon={<MessageCircle size={16} />}
                sx={{ mt: 1 }}
              >
                Iniciar sesión
              </StyledOutlinedButton>
            )}
            
            {sessionActive && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Sesión activa - Tiempo restante:
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold', textAlign: 'center', my: 1 }}>
                  {formatTime(sessionTimeLeft)}
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={100 - (sessionTimeLeft / MAX_SESSION_DURATION * 100)}
                  sx={{ height: 6, borderRadius: 3 }}
                />
              </Box>
            )}
          </Box>
        </Box>
      </ExpertPanelContainer>
    </ChatContainer>
  );

  // Función para renderizar la interfaz de chat adecuada según el dispositivo
  const renderChatInterface = () => {
    if (appState === STATES.WAITING) {
      return renderWaitingState();
    }
    
    if (appState === STATES.ENDED) {
      return renderEndedState();
    }
    
    return isMobile ? renderMobileChatInterface() : renderDesktopChatInterface();
  };

  // Main render method
  return (
    <Box sx={{ py: 0 }}>
      {/* Render appropriate view based on app state */}
      {appState === STATES.LOADING && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}
      
      {appState === STATES.ONBOARDING && renderOnboardingWizard()}
      
      {(appState === STATES.CHATTING || appState === STATES.WAITING || appState === STATES.ENDED) && renderChatInterface()}
      
      {/* Expert panel drawer for mobile */}
      {renderExpertDrawer()}

      {/* Notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ChatConsult;